import React from 'react';
import { Link } from 'react-router-dom';
import {
  Palette, Music, BookOpen, Camera, Sparkles, Users, Trophy,
  ArrowRight, Layers, Target, Heart, Zap
} from 'lucide-react';
import useInView from '../hooks/useInView';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const clubCategories = [
  { icon: Palette, name: 'Digital Art', color: 'from-fuchsia-500 to-pink-600', desc: 'Drawing, illustration, character design, digital painting. Where young artists share work and learn from each other.' },
  { icon: Music, name: 'Music & Mood', color: 'from-violet-500 to-purple-600', desc: 'Playlists, mood boards, song recommendations, music discovery. Built around feelings, not chart positions.' },
  { icon: BookOpen, name: 'Reading Worlds', color: 'from-cyan-500 to-blue-600', desc: 'Books, stories, characters, fan worlds. A space for readers and storytellers to find each other.' },
  { icon: Camera, name: 'Photo & Vision', color: 'from-emerald-500 to-cyan-600', desc: 'Photography, visual storytelling, aesthetics. Sharing how you see the world.' },
  { icon: Sparkles, name: 'Creative Projects', color: 'from-amber-500 to-orange-600', desc: 'DIY, crafts, builds, experiments. The hands-on side of creativity.' },
  { icon: Heart, name: 'Friendship & Identity', color: 'from-rose-500 to-pink-600', desc: 'Conversations about feelings, friendships, self-expression — guided in a child-safe environment.' },
];

const challengeCategories = [
  { icon: Palette, name: 'Creative Challenges', desc: 'Weekly creative prompts — draw, write, design, build something around a shared theme.' },
  { icon: Camera, name: 'Photo Challenges', desc: 'Capture a feeling, a color, a moment. Visual storytelling tasks for the curious.' },
  { icon: Target, name: 'Skill Challenges', desc: 'Try something new — a small step in a real-world skill, just to see if you like it.' },
  { icon: Users, name: 'Friendship Challenges', desc: 'Kind, friendship-positive challenges. Things to do together, not things to compete over.' },
  { icon: Zap, name: 'Mood & Mindfulness', desc: 'Lightweight prompts that help with feelings, focus, and quiet creativity.' },
  { icon: Trophy, name: 'Club Challenges', desc: 'Each club runs its own gentle challenges — sized for that interest, that age, that mood.' },
];

const ageLogic = [
  {
    age: '9–11',
    title: 'Gentle, playful, structured',
    points: [
      'Shorter and simpler creative prompts',
      'More visual, less text-heavy challenges',
      'Friendship-positive framing — no public ranking pressure',
      'More moderated club spaces',
    ],
  },
  {
    age: '12–14',
    title: 'Deeper, more expressive',
    points: [
      'Longer creative prompts and multi-step projects',
      'More nuanced themes around identity and feelings',
      'Stronger creative tools and richer clubs',
      'Same safety logic — calibrated for a slightly older audience',
    ],
  },
];

const physicalDrops = [
  {
    tier: 'Bronze Drop',
    year: 'Year 1',
    image: '/images/rewards/bronze-drop.png',
    tone: 'from-amber-700/30 to-orange-900/20',
    ring: 'border-amber-500/30',
    accent: 'text-amber-300',
    desc: 'The first physical Drop Award in the ROMIND KIDS recognition path. Bronze is not issued simply because a year has passed. It is earned only when the child completes the required first-year conditions: Times discipline, active challenge participation, club activity, creative self-expression, respectful communication, and support for other children.',
    meaning: 'Bronze is the first real proof that a child did more than use the platform — they became an active young creator inside it. If the required virtual medals and achievement conditions are not collected during the year, the Drop is not awarded; the next year becomes a new chance to try again.',
  },
  {
    tier: 'Silver Drop',
    year: 'Year 2',
    image: '/images/rewards/silver-drop.png',
    tone: 'from-slate-400/25 to-slate-800/25',
    ring: 'border-slate-300/30',
    accent: 'text-slate-200',
    desc: 'The second physical Drop Award, available only after Bronze. Silver continues the path with stronger expectations: more consistent participation, deeper club involvement, completed challenge cycles, positive social activity, and a clearer contribution to the ROMIND KIDS community.',
    meaning: 'Silver recognizes growth, loyalty, and social maturity. It marks a child who has not only created for themselves, but has started to influence the space around them through kindness, participation, encouragement, and responsible community behavior.',
  },
  {
    tier: 'Gold Drop',
    year: 'Year 3',
    image: '/images/rewards/gold-drop.png',
    tone: 'from-yellow-500/25 to-amber-900/25',
    ring: 'border-yellow-400/30',
    accent: 'text-yellow-300',
    desc: 'The third-year high-status physical Drop Award. Gold is considered only after Bronze and Silver, and only if the child completes the full third-year achievement path: sustained creativity, regular participation, meaningful challenge results, social contribution, and continued respect for the platform rules.',
    meaning: 'Gold is a visible sign of authority inside the ROMIND KIDS universe. It recognizes a respected creator and a positive example — a child whose voice, work, and attitude can inspire peers in their age group.',
  },
  {
    tier: 'Diamond Drop',
    year: 'Highest Drop Honor',
    image: '/images/rewards/diamond-drop.png',
    tone: 'from-cyan-400/25 to-violet-800/25',
    ring: 'border-cyan-300/30',
    accent: 'text-cyan-200',
    desc: 'The most prestigious physical Drop Award in the ROMIND KIDS recognition system. Diamond is reserved for exceptional long-term achievement, strong social activity, creative leadership, trusted behavior, and a meaningful positive influence inside the community.',
    meaning: 'Diamond is the highest recognition of a child’s contribution to ROMIND KIDS. It honors a young leader of positive influence — someone creative, active, respected, and capable of shaping the mood, values, and direction of their peer community.',
  },
];

const ClubsChallenges = () => (
  <main className="bg-[#05050A] min-h-screen">
    {/* Hero */}
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-violet-600/18 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-cyan-500/12 rounded-full blur-[100px] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel>Clubs & Challenges</SectionLabel>
        <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-6 tracking-tight leading-[1.05] max-w-3xl">
          Where children<br /><span className="gradient-text">create together.</span>
        </h1>
        <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
          The core of ROMIND KIDS isn't a feed — it's clubs and challenges. Spaces designed around interests, not algorithms. Built to replace passive scrolling with purposeful participation.
        </p>
      </div>
    </section>

    {/* Why Clubs */}
    <section className="py-16 bg-[#080714]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <FadeIn>
            <SectionLabel>Why Clubs Exist</SectionLabel>
            <h2 className="font-outfit font-bold text-3xl text-white mb-6 tracking-tight">A different shape of social.</h2>
            <p className="text-white/55 text-sm leading-relaxed mb-4">
              On most platforms, children scroll alone through content optimized for engagement. There is no group, no shared interest, no place that actually belongs to them.
            </p>
            <p className="text-white/55 text-sm leading-relaxed">
              In ROMIND KIDS, clubs are small, interest-based spaces. They are the social layer of the app — built around what a child actually likes, not what an algorithm thinks will keep them watching.
            </p>
          </FadeIn>
          <FadeIn delay={150}>
            <div className="space-y-3">
              {[
                { icon: Layers, title: 'Structure over chaos', desc: 'Each club has a clear topic and tone. Children always know what space they\'re in.' },
                { icon: Users, title: 'Smaller groups, real connection', desc: 'Clubs are designed for genuine creative participation — not for follower counts.' },
                { icon: Heart, title: 'Belonging, not popularity', desc: 'You join because you love the topic. Not because the platform optimized your attention.' },
              ].map((item, i) => (
                <div key={i} className="glass-card p-5 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/25 flex items-center justify-center flex-shrink-0">
                    <item.icon size={15} className="text-violet-300" />
                  </div>
                  <div>
                    <p className="font-outfit font-semibold text-white text-sm mb-0.5">{item.title}</p>
                    <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>

    {/* Club Categories */}
    <section data-testid="club-categories-section" className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Club Categories</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight max-w-2xl">Spaces built around how children actually feel and create.</h2>
          <p className="text-white/50 text-sm mb-10 max-w-xl">A curated set of starter club categories. The library will expand as the platform grows — always within safe, age-aware boundaries.</p>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clubCategories.map((c, i) => (
            <FadeIn key={c.name} delay={i * 60}>
              <div data-testid={`club-category-${i}`} className="glass-card p-6 h-full">
                <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-4 shadow-lg`}>
                  <c.icon size={19} className="text-white" />
                </div>
                <h3 className="font-outfit font-semibold text-white text-base mb-2">{c.name}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{c.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* Why Challenges Exist */}
    <section className="py-16 bg-[#080714]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Why Challenges Exist</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight max-w-2xl">A reason to create, not just consume.</h2>
          <p className="text-white/55 text-sm leading-relaxed mb-10 max-w-3xl">
            Challenges turn a passive moment ("I'm bored, I'll scroll") into a creative one ("I have an idea, I'll try it"). They are gentle prompts — not competitive races. The goal is participation, not winning.
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {challengeCategories.map((c, i) => (
            <FadeIn key={c.name} delay={i * 50}>
              <div data-testid={`challenge-category-${i}`} className="glass-card p-5 h-full">
                <div className="w-9 h-9 rounded-xl bg-purple-500/15 border border-purple-500/25 flex items-center justify-center mb-3">
                  <c.icon size={15} className="text-purple-300" />
                </div>
                <h3 className="font-outfit font-semibold text-white text-sm mb-1.5">{c.name}</h3>
                <p className="text-white/45 text-xs leading-relaxed">{c.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* Age-aware logic */}
    <section data-testid="age-logic-section" className="py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Age-Aware Challenge Logic</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight max-w-2xl">A 9-year-old and a 14-year-old are not the same user.</h2>
          <p className="text-white/55 text-sm leading-relaxed mb-10 max-w-3xl">
            ROMIND KIDS treats 9–11 and 12–14 as two related but distinct experiences. Challenges, club tone, and creative depth shift between the groups — while the safety logic stays constant.
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {ageLogic.map((a, i) => (
            <FadeIn key={a.age} delay={i * 100}>
              <div className="glass-card p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-outfit font-bold text-purple-400 tracking-[0.2em] uppercase">Ages {a.age}</span>
                </div>
                <h3 className="font-outfit font-bold text-white text-xl mb-3">{a.title}</h3>
                <ul className="space-y-2">
                  {a.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-xs text-white/55">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400/70 mt-1.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* Physical Recognition */}
    <section data-testid="physical-recognition-section" className="py-16 bg-[#080714]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Physical Recognition Awards</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight max-w-3xl">
            Real Drop Awards — earned, collected, and recognized.
          </h2>
          <p className="text-white/58 text-sm sm:text-base leading-relaxed mb-4 max-w-4xl">
            Bronze, Silver, Gold, and Diamond Drops are planned as real physical ROMIND KIDS recognition awards. They are not virtual badges and not casual souvenirs. Each Drop is the final physical symbol of a long achievement path built from virtual medals, Times-related discipline, challenge completion, club activity, creative self-expression, respectful communication, and positive social contribution.
          </p>
          <p className="text-white/48 text-sm leading-relaxed mb-4 max-w-4xl">
            A Drop is not given to everyone automatically. A child must meet the required conditions for that level. If the conditions are not completed — if the needed medals, activity, behavior, or contribution are not collected — the physical Drop is not issued that year. The next year becomes a new chance to grow, participate, and try again.
          </p>
          <p className="text-white/42 text-xs leading-relaxed mb-10 max-w-4xl">
            These awards are planned as mailed physical honors sent to the parent or guardian only after eligibility review, parental consent, shipping details, and final program rules are confirmed. They are not a lottery, not a paid prize draw, and not a real-money reward mechanic — they are ROMIND KIDS recognition for earned effort, creativity, loyalty, and positive influence.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {physicalDrops.map((d, i) => (
            <FadeIn key={d.tier} delay={i * 80}>
              <div data-testid={`drop-tier-${i}`} className={`relative overflow-hidden rounded-3xl p-5 h-full bg-gradient-to-br ${d.tone} border ${d.ring}`}>
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-white/10 rounded-full blur-[60px] pointer-events-none" />
                <div className="relative">
                  <div className="aspect-square rounded-2xl bg-black/20 border border-white/10 flex items-center justify-center mb-5 overflow-hidden">
                    <img
                      src={d.image}
                      alt={`${d.tier} physical ROMIND KIDS award`}
                      className="w-full h-full object-contain p-4 drop-shadow-[0_18px_35px_rgba(0,0,0,0.45)]"
                      loading="lazy"
                      draggable="false"
                    />
                  </div>

                  <p className={`text-[11px] font-outfit font-bold tracking-[0.22em] uppercase mb-2 ${d.accent}`}>
                    {d.year} Recognition
                  </p>
                  <h3 className="font-outfit font-bold text-white text-xl mb-3">{d.tier}</h3>
                  <p className="text-white/60 text-xs leading-relaxed mb-4">{d.desc}</p>
                  <div className="rounded-2xl bg-black/18 border border-white/8 p-3">
                    <p className="text-white/48 text-xs leading-relaxed">{d.meaning}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={260}>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="glass-card p-5">
              <h3 className="font-outfit font-semibold text-white text-sm mb-2">Built from earned milestones</h3>
              <p className="text-white/50 text-xs leading-relaxed">
                A Drop is assembled through the child’s annual path: virtual medals, Times habits, challenge results, club participation, creativity, safe behavior, and real social contribution.
              </p>
            </div>
            <div className="glass-card p-5">
              <h3 className="font-outfit font-semibold text-white text-sm mb-2">Recognition among peers</h3>
              <p className="text-white/50 text-xs leading-relaxed">
                Receiving a Drop marks the child as a recognized ROMIND KIDS creator — someone whose effort, kindness, creativity, and activity have earned respect inside their age group.
              </p>
            </div>
            <div className="glass-card p-5">
              <h3 className="font-outfit font-semibold text-white text-sm mb-2">A trusted voice in the community</h3>
              <p className="text-white/50 text-xs leading-relaxed">
                Higher Drop levels recognize children who become positive young opinion leaders: active, creative, helpful, respected, and capable of influencing the community in a healthy direction.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <h2 className="font-outfit font-bold text-2xl text-white mb-4">Want to see clubs & challenges in early testing?</h2>
          <p className="text-white/50 text-sm mb-6 max-w-xl mx-auto">Parents can register for the curated early testing list. We'll be in touch as the program opens.</p>
          <Link to="/testing" data-testid="clubs-testing-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/85 border border-purple-400/40 text-white hover:bg-purple-500 transition-all">
            Join Testing Program <ArrowRight size={14} />
          </Link>
        </FadeIn>
      </div>
    </section>
  </main>
);

export default ClubsChallenges;
